// apps/client/src/pages/leads/leadIngestUrl.ts
//
// ONE builder for the public lead-ingest webhook URL.
//
// Every surface that showed this URL built it by hand and three of the four
// left out the company id, so the URL they told integrators to paste 400'd
// with "companyId Required". leadIngest reads the company id from `c` (or
// `companyId`) and the key from `token`, both on the query string, because a
// no-code form tool cannot set a body or a header. See
// services/client-functions/src/leads/normalizeWebhookLead.ts and
// services/client-functions/src/leads/leadIngest.ts (which additionally reads
// the optional `officeId` / `source` hints to pick the scoped token config).
//
// The path is the Firebase Hosting rewrite /api/leadIngest, not the raw Cloud
// Run URL: the org policy blocks allUsers IAM on Cloud Run, so the direct
// functions URL returns 403.

export interface LeadIngestUrlParams {
  // Nullable on purpose: callers hold these as "not loaded yet" state, and
  // an incomplete URL must come back as '' rather than force a guard at
  // every call site.
  companyId: string | null | undefined;
  token: string | null | undefined;
  /** Per-source token key (website_form, facebook_lead_ad, ...). */
  source?: string | null;
  /** Per-office routing hint; selects the office's webhook config. */
  officeId?: string | null;
}

/** Origin-relative fallback keeps this safe to call during an SSR-less first render. */
export function leadIngestBaseUrl(): string {
  return typeof window !== 'undefined' && window.location
    ? `${window.location.origin}/api/leadIngest`
    : '/api/leadIngest';
}

/**
 * Complete, ready-to-paste ingest URL. Returns '' when the company id or the
 * token is missing, so a caller can render "generate a token first" instead of
 * handing out a URL that cannot work.
 */
export function buildLeadIngestUrl(params: LeadIngestUrlParams): string {
  const { companyId, token } = params;
  if (!companyId || !token) return '';

  const qs = new URLSearchParams();
  // `c` first: it is the one parameter leadIngest cannot do without.
  qs.set('c', companyId);
  if (params.officeId) qs.set('officeId', params.officeId);
  if (params.source) qs.set('source', params.source);
  qs.set('token', token);

  return `${leadIngestBaseUrl()}?${qs.toString()}`;
}
